import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { Lesson, Term } from '@court-sync/shared';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService } from '@/services/coach';
import { PaginatedTable, type PaginatedTableColumn } from '@/components/ui';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { BookOpen, Calendar, Clock, Users } from 'lucide-react';
import { formatTimeHHMM } from '@/utils/timeFormat';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

type CoachLessonRow = {
    lesson: Lesson;
    term: Term | null;
};

function formatLessonDateRange(start: Date, end: Date): string {
    if (start.getTime() === end.getTime()) {
        return format(start, 'MMM d, yyyy');
    }
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
}

function getCampDateRange(lesson: Lesson): { start: Date; end: Date } | null {
    if (lesson.startDate && lesson.endDate) {
        return { start: new Date(lesson.startDate), end: new Date(lesson.endDate) };
    }
    const sessionDates = lesson.campSessionDates;
    if (sessionDates && sessionDates.length > 0) {
        const sorted = [...sessionDates].sort();
        return { start: new Date(sorted[0]), end: new Date(sorted[sorted.length - 1]) };
    }
    if (lesson.startDate) {
        const start = new Date(lesson.startDate);
        return { start, end: start };
    }
    return null;
}

function getLessonDatesDisplay(lesson: Lesson, term: Term | null): { primary: string; secondary?: string } {
    if (term) {
        const range =
            term.startDate && term.endDate
                ? formatLessonDateRange(new Date(term.startDate), new Date(term.endDate))
                : undefined;
        return { primary: term.name, secondary: range };
    }

    if (lesson.lessonType === 'camp') {
        const range = getCampDateRange(lesson);
        return range
            ? { primary: formatLessonDateRange(range.start, range.end) }
            : { primary: '—' };
    }

    if (lesson.lessonType === 'private') {
        if (lesson.startDate && lesson.endDate) {
            return {
                primary: formatLessonDateRange(new Date(lesson.startDate), new Date(lesson.endDate)),
            };
        }
        if (lesson.startDate && lesson.numberOfSessions) {
            return {
                primary: format(new Date(lesson.startDate), 'MMM d, yyyy'),
                secondary: `${lesson.numberOfSessions} sessions`,
            };
        }
        if (lesson.startDate) {
            return { primary: format(new Date(lesson.startDate), 'MMM d, yyyy') };
        }
    }

    return { primary: '—' };
}

function lessonTypeBadgeClass(lessonType: Lesson['lessonType']): string {
    if (lessonType === 'group') {
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
    }
    if (lessonType === 'camp') {
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200';
    }
    return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
}

function LessonNameCell({ lesson }: { lesson: Lesson }) {
    const skillLevel = lesson.skillLevel && lesson.skillLevel !== 'any' ? lesson.skillLevel : null;

    return (
        <div className="min-w-0 max-w-[16rem]">
            <p className="truncate font-medium text-foreground" title={lesson.name}>
                {lesson.name}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span
                    className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize leading-none',
                        lessonTypeBadgeClass(lesson.lessonType)
                    )}
                >
                    {lesson.lessonType}
                </span>
                {skillLevel ? (
                    <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize leading-none text-muted-foreground">
                        {skillLevel}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

function LessonDatesCell({ lesson, term }: { lesson: Lesson; term: Term | null }) {
    const { primary, secondary } = getLessonDatesDisplay(lesson, term);

    return (
        <div className="flex min-w-0 max-w-[12rem] items-start gap-2">
            <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{primary}</p>
                {secondary ? (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{secondary}</p>
                ) : null}
            </div>
        </div>
    );
}

function LessonScheduleCell({ lesson }: { lesson: Lesson }) {
    const days = lesson.daysOfWeek?.map((d) => DAY_NAMES[d]).join(', ') ?? '—';
    const time = `${formatTimeHHMM(lesson.startTime)}–${formatTimeHHMM(lesson.endTime)}`;

    return (
        <div className="flex min-w-0 max-w-[11rem] items-start gap-2">
            <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{days}</p>
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">{time}</p>
            </div>
        </div>
    );
}

function EnrollmentCell({ lesson }: { lesson: Lesson }) {
    const enrolled = lesson.currentEnrollment ?? 0;
    const capacity = lesson.maxCapacity;
    const fillPct = capacity > 0 ? Math.min(100, (enrolled / capacity) * 100) : 0;
    const isFull = capacity > 0 && enrolled >= capacity;

    return (
        <div className="flex min-w-[5.5rem] items-center gap-2.5">
            <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:hidden" aria-hidden />
            <div className="min-w-0 flex-1">
                <p className={cn('text-sm tabular-nums', isFull ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-foreground')}>
                    {enrolled}
                    <span className="text-muted-foreground">/{capacity}</span>
                </p>
                <div
                    className="mt-1 hidden h-1 w-full max-w-[4.5rem] overflow-hidden rounded-full bg-muted sm:block"
                    role="presentation"
                >
                    <div
                        className={cn(
                            'h-full rounded-full transition-[width]',
                            isFull ? 'bg-amber-500' : enrolled > 0 ? 'bg-primary/80' : 'bg-muted-foreground/25'
                        )}
                        style={{ width: `${fillPct}%` }}
                    />
                </div>
            </div>
        </div>
    );
}

function CoachLessonMobileCard({ row }: { row: CoachLessonRow }) {
    const { lesson, term } = row;
    const dates = getLessonDatesDisplay(lesson, term);
    const days = lesson.daysOfWeek?.map((d) => DAY_NAMES[d]).join(', ') ?? '—';
    const enrolled = lesson.currentEnrollment ?? 0;

    return (
        <div className="rounded-lg border border-border bg-card p-4 text-sm shadow-sm">
            <LessonNameCell lesson={lesson} />
            <dl className="mt-3 space-y-2.5 border-t border-border/70 pt-3">
                <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Dates
                    </dt>
                    <dd className="min-w-0 text-right">
                        <p className="text-foreground">{dates.primary}</p>
                        {dates.secondary ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">{dates.secondary}</p>
                        ) : null}
                    </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Schedule
                    </dt>
                    <dd className="min-w-0 text-right text-foreground">
                        <p>{days}</p>
                        <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                            {formatTimeHHMM(lesson.startTime)}–{formatTimeHHMM(lesson.endTime)}
                        </p>
                    </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <dt className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Enrolled
                    </dt>
                    <dd className="tabular-nums text-foreground">
                        {enrolled}/{lesson.maxCapacity}
                    </dd>
                </div>
            </dl>
        </div>
    );
}

export default function CoachLessonsPage() {
    const { academyId } = useCoachAcademyStore();
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [terms, setTerms] = useState<Record<string, Term>>({});
    const [pagination, setPagination] = useState<PaginationState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadPage = useCallback(
        async (nextPage: number) => {
            if (!academyId) return;
            setLoading(true);
            setError(null);
            try {
                const result = await coachService.getCoachLessonsPaginated(academyId, nextPage, PAGE_SIZE);
                setLessons(result.data);
                setTerms(result.terms);
                setPagination(result.pagination);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load lessons');
            } finally {
                setLoading(false);
            }
        },
        [academyId]
    );

    useEffect(() => {
        if (!academyId) return;
        void loadPage(1);
    }, [academyId, loadPage]);

    const rows = useMemo((): CoachLessonRow[] => {
        return lessons.map((lesson) => ({
            lesson,
            term: lesson.termId ? terms[lesson.termId] ?? null : null,
        }));
    }, [lessons, terms]);

    const columns = useMemo((): PaginatedTableColumn<CoachLessonRow>[] => {
        return [
            {
                key: 'lesson',
                header: 'Lesson',
                className: 'pl-4 pr-3 py-3 min-w-[12rem]',
                render: (row) => <LessonNameCell lesson={row.lesson} />,
            },
            {
                key: 'dates',
                header: 'Dates',
                className: 'px-3 py-3',
                render: (row) => <LessonDatesCell lesson={row.lesson} term={row.term} />,
            },
            {
                key: 'schedule',
                header: 'Schedule',
                className: 'px-3 py-3',
                render: (row) => <LessonScheduleCell lesson={row.lesson} />,
            },
            {
                key: 'enrollment',
                header: 'Enrolled',
                className: 'px-3 py-3 pr-4',
                render: (row) => <EnrollmentCell lesson={row.lesson} />,
            },
        ];
    }, []);

    const resultsFrom = pagination ? (pagination.page - 1) * pagination.limit + 1 : 0;
    const resultsTo = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;

    if (!academyId) {
        return (
            <div className="flex items-center justify-center py-16">
                <p className="text-gray-500 dark:text-gray-400">Select an academy to view lessons.</p>
            </div>
        );
    }

    return (
        <div className="contents">
            <PageHeader
                title="My Lessons"
                help={<p>All lessons assigned to you at this academy</p>}
            />

            {error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">{error}</div>
            )}

            {!loading && !error && pagination?.total === 0 ? (
                <DashboardEmptyStateCard>
                    <DashboardEmptyStateIcon icon={BookOpen} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No lessons yet</h3>
                    <p className="text-gray-500 dark:text-gray-400">No lessons assigned to you at this academy.</p>
                </DashboardEmptyStateCard>
            ) : (
                <div className="space-y-3">
                    {pagination && pagination.total > 0 && (
                        <p className="text-sm text-muted-foreground">
                            Showing {resultsFrom}–{resultsTo} of {pagination.total} lessons
                        </p>
                    )}
                    <DashboardDataCard>
                            <PaginatedTable<CoachLessonRow>
                                columns={columns}
                                data={rows}
                                pagination={pagination}
                                loading={loading}
                                emptyMessage="No lessons assigned to you yet."
                                onPageChange={(nextPage) => void loadPage(nextPage)}
                                getRowKey={(row) => row.lesson.id}
                                noWrapperPadding
                                className="px-0 pb-2 sm:pb-3"
                                mobileTitleColumnKey=""
                                renderMobileCard={(row) => <CoachLessonMobileCard row={row} />}
                            />
                    </DashboardDataCard>
                </div>
            )}
        </div>
    );
}
